import { forEach, includes, keys, some, toLower } from 'lodash-es';

import { getQueryValue } from 'common/hooks/use-query-string.hook';

import { getLDFlag } from './launchDarkly';
import { LD_FLAG_DATA_CHANGE_LOG, LD_FLAG_USE_TASK_SYSTEM } from './launchDarklyFlags';

const isFxUrlOverrideAllowed = process.env.REACT_APP_FX_ALLOW_URL_FEATUREFLAG_OVERRIDE === 'true';

let backendFxIntegrationValue: boolean | null = null; // Rework to use hooks

export const FEATURE_FLAG_URL_PREFIX = 'ff_';

/** Flags resolved from the static `defaultFlags` record below. */
export type LocalFeatureFlag =
    | 'editDataCollectionDate'
    | 'editVisitDate'
    | 'printSiteFooter'
    | 'editTranscription'
    | 'sso'
    | 'okta'
    | 'tableauCustomReports'
    | 'tableauStandardReports'
    | 'fxIntegration'
    | 'patientHistory'
    | 'connectorApp';

/** Flags resolved only from LaunchDarkly. Deliberately absent from `defaultFlags`. */
export type LDBackedFeatureFlag = 'use_task_system' | 'dataChangeLog';

export type FeatureFlag = LocalFeatureFlag | LDBackedFeatureFlag;

export const urlQueryParamFlags: LocalFeatureFlag[] = ['fxIntegration', 'connectorApp'];

type FeatureFlagsType = Record<LocalFeatureFlag, boolean>;

export const defaultFlags: FeatureFlagsType = {
    editDataCollectionDate: false,
    editVisitDate: false,
    printSiteFooter: false,
    editTranscription: false,
    sso: true,
    okta: true,
    patientHistory: true,
    tableauCustomReports: true,
    tableauStandardReports: true,
    fxIntegration: true, // BE initialized
    connectorApp: false,
};

export function createFeatureFlags(): FeatureFlagsType {
    const flags = { ...defaultFlags };

    forEach(keys(flags), (flag) => {
        if (includes(urlQueryParamFlags, flag as LocalFeatureFlag)) {
            if (flag === 'fxIntegration' && !isFxUrlOverrideEnabled()) {
                return;
            }

            const paramValue =
                getQueryValue(`${FEATURE_FLAG_URL_PREFIX}${flag}`) ||
                getQueryValue(toLower(`${FEATURE_FLAG_URL_PREFIX}${flag}`));
            if (paramValue === 'true') {
                flags[flag as LocalFeatureFlag] = true;
            } else if (paramValue === 'false') {
                flags[flag as LocalFeatureFlag] = false;
            }
        }
    });

    return flags;
}

export const featureFlags = createFeatureFlags();

/** LaunchDarkly key for each LD-backed flag, so `isFeatureEnabled` has no local default to drift from. */
const ldBackedFlagKeys: Record<LDBackedFeatureFlag, string> = {
    use_task_system: LD_FLAG_USE_TASK_SYSTEM,
    dataChangeLog: LD_FLAG_DATA_CHANGE_LOG,
};

export function isFeatureEnabled(featureFlag: FeatureFlag): boolean {
    const ldFlagKey = ldBackedFlagKeys[featureFlag as LDBackedFeatureFlag];

    // LaunchDarkly is the only source for LD-backed flags, so no `ff_` override or local default applies.
    if (ldFlagKey) {
        return getLDFlag(ldFlagKey, false);
    }

    return featureFlags[featureFlag as LocalFeatureFlag] || false;
}

export function areAnyFeaturesEnabled(...flags: FeatureFlag[]): boolean {
    return some(flags, (flag) => isFeatureEnabled(flag));
}

export function isUrlQueryParamFlag(key: string): boolean {
    return some(urlQueryParamFlags, (flag) => toLower(key) === toLower(`${FEATURE_FLAG_URL_PREFIX}${flag}`));
}

export function isFxUrlOverrideEnabled(): boolean {
    if (backendFxIntegrationValue === false) {
        return false;
    }

    return isFxUrlOverrideAllowed;
}

export function setBackendFxIntegrationEnabled(enabled: boolean): void {
    backendFxIntegrationValue = enabled;
    defaultFlags.fxIntegration = enabled;

    if (!enabled) {
        featureFlags.fxIntegration = false;
    }
}

export const FEATURE_FLAGS_STORAGE_KEY = 'ciFeatureFlags';
